import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Flex, Heading, HStack, Image, Spinner, Text, VStack, useColorModeValue,
} from '@chakra-ui/react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { acceptInvitation, previewInvitation } from '../../api/endpointsBackend';
import { useAuth } from '../../contexts/AuthContext';
import { usePreferences } from '../../contexts/PreferencesContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import { persistWorkspaceId } from '../../auth/workspaceService';
import { hostName } from '../../utils/hostName';
import { formatDateLong } from '../../utils/intlFormatters';
import OwnerAvatar from '../shared/OwnerAvatar';
import RoleChip from '../onboarding/RoleChip';

// "You're already a member" is a successful-enough outcome — the user
// IS a member of the workspace, that's what matters. (Triggered when
// the page reloads mid-flow or the user revisits the link.)
//
// Distinct from "already used and no longer valid" — that's the case
// where the invitation was accepted previously but the user was later
// removed. Token is one-time use; they need a fresh invitation.
const ALREADY_MEMBER_DETAIL = "You're already a member of this workspace";

// The accept endpoint's other refusals, by the detail it sends
// (membership_service.accept_invitation). Matched on the text because the
// status cannot tell them apart: revoked, expired and already used are all
// 409s. "Already used" is a prefix, because that message goes on to say what
// to do. Anything else (the member cap, a network failure) is `generic`.
const ACCEPT_REFUSALS = [
  ['Invitation revoked', 'revoked'],
  ['Invitation expired', 'expired'],
  ['This invitation was issued for a different email address', 'otherEmail'],
  ['This invitation was already used', 'used'],
];

// The preview statuses that are already a refusal; the same kinds as above.
const PREVIEW_REFUSALS = ['expired', 'used', 'revoked'];

function refusalKind(err) {
  if (err?.response?.status === 404) return 'notFound';
  const detail = err?.response?.data?.detail;
  if (typeof detail !== 'string') return 'generic';
  const hit = ACCEPT_REFUSALS.find(([text]) => detail.startsWith(text));
  return hit ? hit[1] : 'generic';
}

// The login and signup screens' one blue action, and a quiet second one.
const PRIMARY = {
  w: 'full',
  size: 'lg',
  bg: 'redesign.accent.fill',
  color: 'white',
  fontWeight: 'medium',
  borderRadius: 'lg',
  _hover: { opacity: 0.9 },
  _active: { opacity: 0.8 },
};
const SECONDARY = {
  w: 'full',
  size: 'lg',
  variant: 'ghost',
  color: 'redesign.text.muted',
  fontWeight: 'medium',
  borderRadius: 'lg',
  _hover: { bg: 'redesign.bg.hover', color: 'redesign.text' },
};

// A full page load, not navigate(): workspaces and preferences are read once
// per load, and both just changed on the server. `/` is the start page, whose
// guards decide where this person belongs now (the guest flow, the owner flow,
// or the workspace).
const goHome = () => { window.location.href = '/'; };

/**
 * The login and signup card, so the three screens read as one product: 440px,
 * the app icon on top. Here the host's photo sits on the icon's corner once
 * the preview has said who they are.
 */
function InviteCard({ preview, children }) {
  const iconSrc = useColorModeValue('/brand/app-icon-light.svg', '/brand/app-icon-dark.svg');
  return (
    <Flex minH="100vh" align="center" justify="center" bg="redesign.bg.page" px={4}>
      <Flex
        bg="redesign.bg.surface"
        borderWidth="1px"
        borderColor="redesign.border"
        borderRadius="2xl"
        boxShadow="card"
        w="full"
        maxW="440px"
        minH={{ base: 'auto', md: '520px' }}
        px={{ base: 8, md: 12 }}
        py={{ base: 12, md: 16 }}
        direction="column"
        align="center"
        justify="center"
      >
        <VStack spacing={5} w="full">
          <Box position="relative" boxSize="56px" flexShrink={0}>
            <Image src={iconSrc} alt="Mi Kapital" boxSize="56px" borderRadius="14px" />
            {preview && (
              // Ringed in the card's own colour so it reads as sitting on the
              // icon rather than cut into it. Width and style as longhands,
              // the colour through `sx`: Avatar moves a `borderColor` prop
              // into its base styles, and a `border` shorthand written after
              // them reset the colour to currentColor, the heading colour,
              // which drew a near-black ring (a white one in dark mode).
              <OwnerAvatar
                workspace={preview}
                size={34}
                position="absolute"
                right="-12px"
                bottom="-8px"
                borderWidth="3px"
                borderStyle="solid"
                sx={{ borderColor: 'redesign.bg.surface' }}
              />
            )}
          </Box>
          {children}
        </VStack>
      </Flex>
    </Flex>
  );
}

function CardTitle({ children }) {
  return (
    <Heading
      as="h1"
      size="md"
      fontWeight="semibold"
      letterSpacing="-0.02em"
      color="redesign.heading"
      textAlign="center"
    >
      {children}
    </Heading>
  );
}

function CardBody({ children }) {
  return (
    <Text color="redesign.text.muted" fontSize="sm" textAlign="center" lineHeight="1.6">
      {children}
    </Text>
  );
}

function DetailRow({ label, children, first }) {
  return (
    <HStack
      justify="space-between"
      spacing={4}
      py={3}
      borderTopWidth={first ? 0 : '1px'}
      borderTopColor="redesign.border"
    >
      <Text fontSize="sm" color="redesign.text.muted" flexShrink={0}>{label}</Text>
      <Box minW={0} fontSize="sm" fontWeight="semibold" color="redesign.text" textAlign="right">
        {children}
      </Box>
    </HStack>
  );
}

/**
 * Signed out, with a live invitation: who is inviting you, to what, as which
 * role and at which address, and the one way in. Everything comes from the
 * preview; nothing is accepted until they are signed in.
 */
function Landing({ preview, name, onContinue }) {
  const { t } = useTranslation('workspaces');
  return (
    <>
      <CardTitle>
        {name ? t('invitationAccept.landing.title', { name }) : t('invitationAccept.landing.titleNoName')}
      </CardTitle>
      <CardBody>{t('invitationAccept.landing.body')}</CardBody>
      {preview && (
        <Box w="full" borderWidth="1px" borderColor="redesign.border" borderRadius="xl" px={4} py={1}>
          <DetailRow label={t('invitationAccept.landing.space')} first>
            <Text noOfLines={1}>{preview.workspace_name}</Text>
          </DetailRow>
          <DetailRow label={t('invitationAccept.landing.role')}>
            <RoleChip role={preview.workspace_role} />
          </DetailRow>
          <DetailRow label={t('invitationAccept.landing.for')}>
            <Text noOfLines={1}>{preview.invited_email_masked}</Text>
          </DetailRow>
        </Box>
      )}
      <Box w="full" pt={2}>
        <Button {...PRIMARY} onClick={onContinue}>
          {t('auth:login.continueWithGoogle')}
        </Button>
      </Box>
      {preview?.expires_at && (
        <Text fontSize="xs" color="redesign.text.muted" textAlign="center">
          {t('invitationAccept.landing.expires', { date: formatDateLong(preview.expires_at) })}
        </Text>
      )}
    </>
  );
}

/**
 * One screen per way an invitation can fail, each with a way forward and a
 * line under it saying what happens next. The host is named from the preview;
 * the "NoName" copy covers a preview that could not be read.
 *
 * `signedIn` decides the lines that assume an account ("your account is
 * already created"). `onSignIn`, offered only signed out, is for the one case
 * the preview cannot settle alone (below).
 */
function Refusal({
  kind, preview, name, currentEmail, signedIn, onSwitchAccount, onSignIn,
}) {
  const { t } = useTranslation('workspaces');
  const k = (key) => `invitationAccept.${kind}.${key}`;

  let title;
  let body;
  let actions;
  let foot = null;
  if (kind === 'expired' || kind === 'used' || kind === 'revoked') {
    title = kind === 'revoked' && !name ? t(k('titleNoName')) : t(k('title'), { name });
    body = name ? t(k('body'), { name }) : t(k('bodyNoName'));
    // They are a Mi Kapital user now either way; for a first-run one, `/` is
    // the owner flow, which is right: their own space is all they have.
    actions = (
      <>
        <Button {...PRIMARY} onClick={goHome}>{t('invitationAccept.ownSpace')}</Button>
        {/* Signed out, "used" is read off the invitation alone, and its own
            invitee may still be a member (they accepted on another device,
            say). Signed in, the accept answers "already a member" for them,
            which is the success card, so they are offered the sign-in that
            lets the accept decide. Someone who was removed gets this screen
            again. */}
        {kind === 'used' && onSignIn && (
          <Button {...SECONDARY} onClick={onSignIn}>{t('auth:login.continueWithGoogle')}</Button>
        )}
      </>
    );
    if (kind === 'expired') foot = name ? t(k('foot'), { name }) : t(k('footNoName'));
    else if (signedIn) foot = t(k('foot'));
    else if (onSignIn) foot = t(k('signedOutHint'));
  } else if (kind === 'otherEmail') {
    title = t(k('title'));
    body = t(k('body'), {
      invited: preview?.invited_email_masked || '•••',
      current: currentEmail || '',
    });
    // Continuing leaves the invitation pending: its owner can still use it.
    actions = (
      <>
        <Button {...PRIMARY} onClick={onSwitchAccount}>{t(k('switchAccount'))}</Button>
        <Button {...SECONDARY} onClick={goHome}>{t(k('continue'))}</Button>
      </>
    );
    foot = t(k('foot'));
  } else {
    title = t(k('title'));
    body = t(k('body'));
    actions = <Button {...PRIMARY} onClick={goHome}>{t('invitationAccept.backToApp')}</Button>;
  }

  return (
    <>
      <CardTitle>{title}</CardTitle>
      <CardBody>{body}</CardBody>
      <VStack spacing={2} w="full" pt={2}>{actions}</VStack>
      {foot && (
        <Text fontSize="13px" lineHeight="1.5" color="redesign.text.muted" textAlign="center">
          {foot}
        </Text>
      )}
    </>
  );
}

function Waiting({ label }) {
  return (
    <HStack spacing={3} pt={2} color="redesign.text.muted">
      <Spinner size="sm" color="redesign.accent.blue" thickness="2px" />
      {label && <Text fontSize="sm">{label}</Text>}
    </HStack>
  );
}

/**
 * Invitation accept page (MI-268; redesigned for invited members).
 *
 * Route: /invitations/accept?token=<raw>. Outside ProtectedRoute on purpose:
 * it is also the landing for someone who is not signed in yet.
 *
 * - Signed out: the preview says who is inviting whom, to what, with which
 *   role, or which way the invitation has already failed. "Continue with
 *   Google" signs in with this URL as the return path; a brand-new identity
 *   goes through /signup, which brings them back here.
 * - Signed in: the invitation is accepted on arrival. A first-run user is sent
 *   straight on to `/`, where onboarding holds them in the guest flow; anyone
 *   else gets the success card, as before.
 */
const InvitationAcceptPage = () => {
  const [params] = useSearchParams();
  const token = params.get('token');
  const invitePath = token ? `/invitations/accept?token=${encodeURIComponent(token)}` : null;
  const { isAuthenticated, loading: authLoading, login, logout, user } = useAuth();
  const { onboarding } = usePreferences();
  const { refresh, workspaces } = useWorkspace();
  const { t } = useTranslation('workspaces');

  // 'loading' | 'ready' | 'notFound' | 'failed'. Read signed in too: the error
  // screens name the host from it.
  const [preview, setPreview] = useState({ status: 'loading', data: null });
  // 'idle' | 'accepting' | 'leaving' | 'success' | 'error', with the role on
  // success and the kind on error. Outcomes are kept as data and translated
  // at render, so a language change never has to re-run the accept.
  const [accept, setAccept] = useState({ status: 'idle' });

  // The accept fires once per visit (see below), so its callback is a closure
  // over the render it fired from. Preferences hydrate in that same commit,
  // after this page's effects run, so that closure may hold the cached map
  // from before hydration. The ref is read when the answer arrives instead.
  const onboardingRef = useRef(onboarding);
  onboardingRef.current = onboarding;
  // The list as it was before this accept: whether an earlier guest flow's
  // space is still theirs, the other half of the backend's test below.
  const workspacesRef = useRef(workspaces);
  workspacesRef.current = workspaces;
  const attempted = useRef(false);

  useEffect(() => {
    if (!token) return undefined;
    let live = true;
    previewInvitation(token)
      .then((data) => { if (live) setPreview({ status: 'ready', data }); })
      .catch((err) => {
        if (!live) return;
        setPreview({ status: err?.response?.status === 404 ? 'notFound' : 'failed', data: null });
      });
    return () => { live = false; };
  }, [token]);

  // Once per visit, guarded by a ref rather than a cancelled flag. React 18
  // runs effects twice in development, and a re-run for any other reason
  // (a language change on hydration, say) used to cancel the first request
  // and send a second: the first had already accepted, so the page showed the
  // second's "already a member" instead, and a first-run user was not sent on.
  useEffect(() => {
    if (!token || authLoading || !isAuthenticated || attempted.current) return;
    attempted.current = true;
    setAccept({ status: 'accepting' });
    acceptInvitation(token)
      .then(async (membership) => {
        // The host's space becomes the active one on the next load, so the
        // guest flow and the workspace both open on it. Not switchWorkspace():
        // its reload would re-run this page against a spent token.
        const ob = onboardingRef.current || {};
        // The backend's own test for writing the invitee marker, read from the
        // map as it was before this accept (the in-memory copy never receives
        // what the backend just wrote): everyone who accepts is marked, new to
        // Mi Kapital or not, unless an unfinished guest flow for another space
        // they are still in is under way. True means the marker is now on the
        // server, and `/` will route them to the guest flow once preferences
        // reload.
        const pending = ob.inviteWorkspaceId;
        const busyElsewhere = pending != null
          && pending !== membership.workspace_id
          && !ob.inviteCompletedAt
          && (workspacesRef.current || []).some((w) => w.id === pending);
        if (!busyElsewhere) {
          persistWorkspaceId(membership.workspace_id);
          setAccept({ status: 'leaving' });
          goHome();
          return;
        }
        await refresh();
        persistWorkspaceId(membership.workspace_id);
        setAccept({ status: 'success', role: membership.workspace_role });
      })
      .catch((err) => {
        if (err?.response?.data?.detail === ALREADY_MEMBER_DETAIL) {
          setAccept({ status: 'success', alreadyMember: true });
          return;
        }
        setAccept({ status: 'error', kind: refusalKind(err) });
      });
  }, [token, authLoading, isAuthenticated, refresh]);

  const data = preview.status === 'ready' ? preview.data : null;
  const name = hostName(data);
  const refusal = (kind, onSignIn) => (
    <InviteCard preview={data}>
      <Refusal
        kind={kind}
        preview={data}
        name={name}
        currentEmail={user?.email}
        signedIn={isAuthenticated}
        onSwitchAccount={() => logout(invitePath)}
        onSignIn={onSignIn}
      />
    </InviteCard>
  );
  const waiting = (label) => (
    <InviteCard preview={data}>
      <Waiting label={label} />
    </InviteCard>
  );

  if (!token) return refusal('notFound');

  if (authLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg="redesign.bg.page">
        <Spinner size="xl" color="redesign.accent.blue" thickness="4px" />
      </Flex>
    );
  }

  if (isAuthenticated) {
    if (accept.status === 'success') {
      const message = accept.alreadyMember
        ? t('invitationAccept.alreadyMember')
        : t('invitationAccept.successMessage', { role: t(`roles.${accept.role}`, accept.role) });
      return (
        <InviteCard preview={data}>
          <CardTitle>{t('invitationAccept.successTitle')}</CardTitle>
          <CardBody>{message}</CardBody>
          <Box w="full" pt={2}>
            {/* Hard navigation so data hooks rehydrate against the
                newly-persisted active workspace. See WorkspaceContext for why
                switchWorkspace uses window.location.reload() — same reason
                here. `/` opens the start page: Home on the phone, Movements on
                the desktop. */}
            <Button {...PRIMARY} onClick={goHome}>{t('invitationAccept.openWorkspace')}</Button>
          </Box>
        </InviteCard>
      );
    }
    // A refusal names the host, so it waits for the preview rather than
    // redrawing its title when the name arrives.
    if (accept.status === 'error' && preview.status !== 'loading') return refusal(accept.kind);
    return waiting(t('invitationAccept.loading'));
  }

  // Signed out: only the preview can say anything yet. Its status is derived
  // in the order the accept checks revoked, expired and used, but it cannot
  // know who is asking. So two answers wait for the sign-in: a wrong address,
  // and "already a member", which the accept checks before "used". A used
  // invitation whose invitee still belongs to the space therefore reads as
  // used here, and that screen offers the sign-in that lets the accept decide.
  if (preview.status === 'loading') return waiting(null);
  if (preview.status === 'notFound') return refusal('notFound');
  if (PREVIEW_REFUSALS.includes(data?.status)) {
    return refusal(data.status, data.status === 'used' ? () => login(invitePath) : undefined);
  }
  return (
    <InviteCard preview={data}>
      <Landing preview={data} name={name} onContinue={() => login(invitePath)} />
    </InviteCard>
  );
};

export default InvitationAcceptPage;
